import React, { useEffect, useState } from "react";
import { Avatar, Button, Empty, Modal, Space, Tabs, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, CreditCardOutlined, DollarCircleOutlined, FileTextOutlined, HistoryOutlined, ShopOutlined, UserOutlined } from "@ant-design/icons";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import useEntityModalTheme from "./useEntityModalTheme";
import "./styles.css";

const ENTITY_CONFIG = {
  customer: { badge: "Client", title: "Détails du client", totalLabel: "Total ventes", emptyLabel: "Aucune fiche client disponible", tab: "Fiche client" },
  supplier: { badge: "Fournisseur", title: "Détails du fournisseur", totalLabel: "Total achats", emptyLabel: "Aucune fiche fournisseur disponible", tab: "Fiche fournisseur" },
};
const toSafeNumber = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const hasValue = value => value !== null && value !== undefined && (typeof value !== "string" || value.trim().length > 0);
const renderText = value => hasValue(value) ? value : "Non renseigné";
const formatCurrency = value => `${new Intl.NumberFormat("fr-FR").format(toSafeNumber(value))} CFA`;
const formatDateTime = value => {
  if (!hasValue(value)) return "Non renseigné";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Non renseigné" : date.toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};
const renderLoyaltyStatus = status => {
  const normalized = String(status || "active").toLowerCase().trim();
  if (normalized === "revoked") return <Tag color="error">Révoquée</Tag>;
  if (normalized === "disabled") return <Tag color="warning">Désactivée</Tag>;
  return <Tag color="success" icon={<CheckCircleOutlined aria-hidden />}>Active</Tag>;
};
const DetailRows = ({ items }) => <dl className="entity-detail-rows">{items.map(([label, value]) => (
  <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
))}</dl>;
const EntityMetric = ({ icon, label, value, danger }) => (
  <div className="entity-detail-metric"><div className="entity-detail-metric-label"><span aria-hidden>{icon}</span>{label}</div>
    <strong className={danger ? "entity-detail-metric-danger" : undefined}>{value}</strong></div>
);

export default function EntityDetailsModal({ visible, onCancel, entity, entityType = "customer", resource }) {
  const config = ENTITY_CONFIG[entityType] || ENTITY_CONFIG.customer;
  const style = useEntityModalTheme();
  const [activeTab, setActiveTab] = useState("sheet");
  useEffect(() => { setActiveTab("sheet"); }, [visible, entity?.id, entityType]);
  const enabled = String(entity?.status || "").toLowerCase().trim() !== "disabled";
  const initials = String(entity?.name || config.badge).trim().split(/\s+/).slice(0, 2).map(word => word[0]).join("").toUpperCase();
  const body = entity && <>
    <div className="entity-detail-identity">
      <Avatar shape="square" size={112} style={{ fontSize: 32 }} src={entity.profile_image || undefined} className="entity-detail-avatar">{initials}</Avatar>
      <div className="entity-detail-heading"><h2>{renderText(entity.name)}</h2>
        <p>Établissement <span aria-hidden>·</span> <strong>{renderText(entity.warehouse_name)}</strong></p></div>
      <Space wrap className="entity-detail-badges">
        <Tag color="blue" icon={entityType === "supplier" ? <ShopOutlined aria-hidden /> : <UserOutlined aria-hidden />}>{config.badge}</Tag>
        <Tag color={enabled ? "success" : "default"} icon={enabled ? <CheckCircleOutlined aria-hidden /> : <CloseCircleOutlined aria-hidden />}>{enabled ? "Activé" : "Désactivé"}</Tag>
      </Space>
    </div>
    <div className="entity-detail-metrics" role="region" aria-label="Synthèse financière">
      <EntityMetric icon={<DollarCircleOutlined />} label={config.totalLabel} value={formatCurrency(entity.financial_total_amount)} />
      <EntityMetric icon={<CreditCardOutlined />} label="Montant dû" value={formatCurrency(entity.financial_due_amount)} danger={toSafeNumber(entity.financial_due_amount) > 0} />
      <EntityMetric icon={<FileTextOutlined />} label="Factures impayées" value={new Intl.NumberFormat("fr-FR").format(toSafeNumber(entity.financial_unpaid_count))} />
      <EntityMetric icon={<CreditCardOutlined />} label="Limite de crédit" value={formatCurrency(entity.credit_limit)} />
    </div>
    <Tabs className="entity-detail-tabs" activeKey={activeTab} onChange={setActiveTab} items={[
      { key: "sheet", label: <span><FileTextOutlined aria-hidden /> {config.tab}</span>, children: (
        <div className="entity-detail-columns">
          <section aria-label="Coordonnées"><h3>Coordonnées</h3><DetailRows items={[
            ["Email", renderText(entity.email)], ["Téléphone", renderText(entity.phone)], ["Établissement", renderText(entity.warehouse_name)],
            ["Adresse", renderText(entity.address)], ["Adresse de livraison", renderText(entity.shipping_address)],
          ]} /></section>
          <section aria-label="Informations commerciales"><h3>Informations commerciales</h3><DetailRows items={[
            ["RCCM", renderText(entity.rccm)], ["IFU", renderText(entity.ifu)], ["Période de crédit", `${toSafeNumber(entity.credit_period)} jour(s)`],
            [entity.opening_balance_type === "pay" ? "Solde à payer" : "Solde à recevoir", formatCurrency(entity.opening_balance)],
            ...(entityType === "customer" ? [["Fidélité", renderLoyaltyStatus(entity.loyalty_status)]] : []),
          ]} /></section>
        </div>
      ) },
      { key: "trace", label: <span><HistoryOutlined aria-hidden /> Traçabilité</span>, children: (
        <section className="entity-detail-trace" aria-label="Traçabilité"><h3>Traçabilité</h3><DetailRows items={[
          ["Date de création", formatDateTime(entity.created_at)], ["Créé par", renderText(entity.created_by_name)],
          ["Dernière modification", formatDateTime(entity.updated_at)], ["Modifié par", renderText(entity.last_modified_by_name)],
        ]} /></section>
      ) },
    ]} />
  </>;
  return <Modal open={visible} onCancel={onCancel} centered destroyOnClose width={1180}
    className="entity-modal entity-details-modal" style={style} title={<span className="entity-detail-title">{config.title}</span>}
    footer={<Button type="primary" onClick={onCancel}>Fermer</Button>}>
    {resource && <BusinessResourceNotice resource={resource} label={`la liste des ${entityType === "supplier" ? "fournisseurs" : "clients"}`} />}
    {body || <Empty description={config.emptyLabel} />}
  </Modal>;
}
